/**
 * Typed text: a line is revealed as if a clerk were typing it, with a mono
 * caret at the end (life-layer.md §3 P2, "Service Record typed reveal").
 *
 * Owns: the character-by-character reveal of an element's own text. Must not: translate or change
 * the text (the full string is the element's content from the first frame,
 * so a screen reader and a test read it whole), or decide when a list is
 * due (callers choose). Under reduced motion nothing runs.
 *
 * The reveal hides the not-yet-typed tail by wrapping it in a transparent
 * span rather than removing it, so the line never reflows while it types.
 */
import { reducedMotion } from '@/engine/motion';

/** Per-line reveal length, ms: life-layer.md brief (120-200 ms a line). */
export const TYPE_MIN_MS = 120;
export const TYPE_MAX_MS = 200;
/** How long a line of `length` characters takes: 6 ms a character, clamped to 120-200 ms. */
export function typeDuration(length: number): number {
  return Math.min(TYPE_MAX_MS, Math.max(TYPE_MIN_MS, length * 6));
}

/** The visible head of `text` at progress `p` (0..1): whole characters only. */
export function typedHead(text: string, p: number): string {
  return text.slice(0, Math.floor(text.length * Math.min(1, Math.max(0, p))));
}

/** Type `el`'s own text over `ms`, after `delay`. Returns a cancel that restores plain text. */
export function typeInto(el: HTMLElement, ms: number, delay = 0): () => void {
  const text = el.textContent ?? '';
  if (reducedMotion() || text.length === 0) return () => undefined;
  const head = document.createElement('span');
  const tail = document.createElement('span');
  const caret = document.createElement('span');
  tail.style.opacity = '0';
  caret.textContent = '▌';
  caret.setAttribute('aria-hidden', 'true');
  caret.style.cssText = 'display:inline-block;width:0;overflow:visible;opacity:0.7';
  el.textContent = '';
  el.append(head, caret, tail);
  head.textContent = '';
  tail.textContent = text;

  let raf = 0;
  const finish = (): void => {
    cancelAnimationFrame(raf);
    el.textContent = text;
  };
  const start = performance.now() + delay;
  const tick = (now: number): void => {
    const p = (now - start) / ms;
    if (p >= 1) return finish();
    const shown = typedHead(text, p);
    head.textContent = shown;
    tail.textContent = text.slice(shown.length);
    raf = requestAnimationFrame(tick);
  };
  raf = requestAnimationFrame(tick);
  return finish;
}
